<script setup lang="ts">
import { instructorModule } from '@/store/instructor/module'
import { stripeModule } from '@/store/stripe/module'
import type { StripeEmbeddedCheckout } from '@stripe/stripe-js'
import { type Stripe as StripeNode } from 'stripe'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const props = withDefaults(defineProps<{
    mode: 'payment' | 'setup'
    priceData?: StripeNode.Checkout.SessionCreateParams.LineItem.PriceData
    paymentIntentMetadata?: StripeNode.Checkout.SessionCreateParams['metadata']
}>(), {
    priceData: undefined,
    paymentIntentMetadata: undefined,
})

const emit = defineEmits<{
    'checkoutCompleted': [checkoutSessionId: string]
    'paymentMethodSaved': []
}>()

const checkout = ref<StripeEmbeddedCheckout | null>(null)
const checkoutSessionId = ref<string | null>(null)

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

onMounted(() => {
    nextTick(async () => {
        if (import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY && currentOrgInstructor.value) {
            const { stripeInstance } = await stripeModule.actions.loadStripeState()
            const { clientSecret, checkoutSessionId: newCheckoutSessionId } =
                await stripeModule.actions.createCheckoutSession({
                    mode: props.mode,
                    orgInstructorId: currentOrgInstructor.value.objectId,
                    priceData: props.priceData,
                    paymentIntentMetadata: props.paymentIntentMetadata,
                })
            if (stripeInstance && clientSecret && checkoutSessionId) {
                checkoutSessionId.value = newCheckoutSessionId
                checkout.value = await stripeInstance.initEmbeddedCheckout({
                    clientSecret,
                    onComplete: () => {
                        if (props.mode === 'setup') {
                            paymentMethodSaved()
                        } else {
                            checkoutCompleted()
                        }
                    },
                })
                checkout.value.mount('#checkout')
            }
        }
    })
})

onBeforeUnmount(() => {
    if (checkout.value) {
        checkout.value.destroy()
    }
})

const checkoutCompleted = () => {
    if (checkout.value && checkoutSessionId.value) {
        emit('checkoutCompleted', checkoutSessionId.value)
    }
}

const paymentMethodSaved = () => {
    emit('paymentMethodSaved')
}

</script>

<template>
    <div class="payment-fields">
        <div id="checkout" />
    </div>
</template>

<style lang="scss">
.payment-fields {
    width: 100%;
}
#checkout iframe {
  border-radius: 12px;
}
</style>
